import log from '@vcclive/log';
import { EventEmitter } from 'events';
import { connect } from 'socketcluster-client';
import { LocalDataStore } from './client/LocalDataStore';

const debug = log.getDebug('com');
const emitter = new EventEmitter();
emitter.on('error', (e: Error) => {
    log.error('Error in emitter:', e);
});

let socket: SCSocket;

// noinspection JSUnusedGlobalSymbols
const com = {
    cleanClientId() {
        LocalDataStore.setToStorage('webchatClientId', '');
    },
    init(loginData: LoginData) {
        if (socket) {
            return;
        }
        const hostname = loginData.hostname || process.env.SOCKET_HOSTNAME;
        log.debug(`init, connecting to: ${hostname}`);
        const fullLoginData = { ...loginData, hostname };
        const options = {
            hostname,
            secure: Number(process.env.SOCKET_SECURE) === 1,
            port: loginData.port || (process.env.SOCKET_PORT && +process.env.SOCKET_PORT) || 443,
            autoReconnect: true,
            autoReconnectOptions: {
                initialDelay: 1000,
                randomness: 1000,
            },
        };
        debug(options);
        socket = connect(options);
        socket.on('error', errorHandler);
        socket.on('connect', (status) => {
            log.debug('connected');
            if (!status.isAuthenticated) {
                login(fullLoginData);
            }
        });
        socket.on('authStateChange', (data) => {
            log.debug(`authStateChange: ${data.newState}`);
            debug('authStateChange, %o, %O', data, socket.authState);
            if (data.newState === 'authenticated') {
                return;
            }
            login(fullLoginData);
        });
    },
    onConnect(callback: () => void) {
        socket.on('authStateChange', (data: { newState: string }) => {
            if (data.newState === 'authenticated') {
                callback();
                LocalDataStore.setToStorage('webchatClientId', socket.authToken.clientId);
            }
        });
        if (socket.authState === 'authenticated') {
            callback();
        }
    },
    onDisconnect(callback: () => void) {
        socket.on('disconnect', callback);
    },
    onError(callback: (error: Error) => void) {
        socket.on('error', callback);
    },
    relogin() {
        socket.deauthenticate((error) => {
            if (error) {
                log.debug('logout error');
                log.error(error);
            } else {
                debug('deauthenticate OK');
                log.info('remove clientId from localDataStorage');
                LocalDataStore.setToStorage('webchatClientId', '');
            }
        });
    },
    emit(event: string, data: any, callback?: (error: Error, data: any) => void) {
        return socket.emit(event, data, callback);
    },
    getChannelName() {
        if (socket.authToken.customer) {
            return `${socket.authToken.app}/${socket.authToken.customer}/${socket.authToken.clientId}`;
        }
        return `${socket.authToken.app}/${socket.authToken.clientId}`;
    },
    getRemoteIp() {
        return socket.authToken.remoteIp;
    },
    publish(channelName: string, data: any) {
        debug(`publish data to ${channelName}`);
        socket.publish(channelName, {
            data,
            from: this.getChannelName(),
        }, (error) => {
            if (error) {
                log.error(`Publish error: ${error.message}`, {
                    channelName,
                    data,
                });
            }
        });
    },
    subscribe(channelName: string, handler: SCHandler, options?: SubscribeOptions) {
        const realChannelName = this.realChannelName(channelName);
        debug(`subscribe to ${realChannelName}`);
        const eventName = `watch:${channelName}`;
        const channel = socket.subscribe(realChannelName, options);
        if (channel.watchers().length === 0) {
            channel.watch((event: SCEvent) => {
                log.debug(`message in ${realChannelName}`, event);
                emitter.emit(eventName, event);
            });
        }
        emitter.removeListener(eventName, handler);
        emitter.addListener(eventName, handler);

        return channel;
    },
    unsubscribe(channelName: string) {
        const realChannelName = this.realChannelName(channelName);
        debug(`unsubscribe ${realChannelName}`);
        socket.unwatch(realChannelName);

        return socket.unsubscribe(realChannelName);
    },
    realChannelName(channelName: string) {
        let realChannelName = channelName.replace('{APP}', socket.authToken.app);
        realChannelName = realChannelName.replace('{ID}', socket.authToken.clientId);
        realChannelName = realChannelName.replace('{USER}', this.getChannelName());

        return realChannelName;
    },
};

export default com;

function login(loginData: LoginData) {
    log.debug('com.login');
    const match = /^(.+)\.asp\.virtual-call-center\.eu$/.exec(`${loginData.hostname}`);
    const override: Partial<LoginData> = {};
    const storedClientId = LocalDataStore.getFromStorage<string>('webchatClientId');
    if (storedClientId) {
        override.username = storedClientId;
    }
    socket.emit('login', {
        ...loginData,
        ...override,
        customer: (match && match[1]) || 'ALL',
    }, (err: unknown) => {
        if (err) {
            setTimeout(() => {
                login(loginData);
            }, 10000);
            log.error('Error while logging in, retrying in 10s! Error: ', err);
        }
    });
}

function errorHandler(error: Error) {
    if (error.code === 1006) {
        log.info(`com.error: ${error.message}`);
        return;
    }
    log.error('com.error', error);
}
